import React, {useRef} from "react";
import {useSelector} from "react-redux";
import {css, Theme, useTheme} from "@emotion/react";
import styled from "@emotion/styled";

import {Button} from "@pg-design/button-module";
import {elevation} from "@pg-design/elevation";
import {display, fadeInAnimation, flex, ml, mr, mt, pointer} from "@pg-design/helpers-css";
import {BrandBuildingCubeIcon, BrandMapPinIcon, MapMarkerLocationIcon} from "@pg-design/icons-module";
import {getThemeVariable} from "@pg-design/styles-strings";
import {Tabs} from "@pg-design/tabs";
import {Text} from "@pg-design/text-module";
import {useUserDevice} from "@pg-mono/user-device";
import {Country} from "@pg-rp/common-types";

import {rpFeatureFlagVendorReportDownload} from "../../app/read_rp_environment_variables";
import {IRPStore} from "../../app/rp_reducer";
import {OneColumnGridHolder, OneColumnGridMain} from "../../atoms/OneColumnGrid";
import {useStickyIntersection} from "../../hooks/use_sticky_intersection";
import {GTMEventApplicationSourceSection} from "../../lead/types/GTMEvenApplicationSourceSection";
import {IRangeQuery} from "../../request/IRangeQuery";
import {insertIf} from "../../utils/insert_if";
import {DetailConstructionGalleryCTA} from "../components/DetailConstructionGalleryCTA";
import {VendorReportButton} from "../components/VendorReportButton";
import {IOfferDetailPromotion} from "../detail/actions/fetch_offer_detail_promotions";
import {Planner3dModal} from "../detail/planner3D/components/Planner3D";
import {OfferDisplayType} from "../helpers/OfferDisplayType";
import {getOfferTypeNamePluralCapital} from "../helpers/OfferType";
import {OfferModalActiveTab} from "../types/OfferModalActiveTab";
import {OfferType} from "../types/OfferType";

interface IProps {
    refElements: {
        refGallery: React.RefObject<HTMLTableSectionElement | null>;
        refProperties?: React.RefObject<HTMLTableSectionElement | null>;
        refDescription: React.RefObject<HTMLTableSectionElement | null>;
        refPromotions?: React.RefObject<HTMLTableSectionElement | null>;
        refLocation: React.RefObject<HTMLTableSectionElement | null>;
        refContact?: React.RefObject<HTMLTableSectionElement | null>;
    };
    promotions?: IOfferDetailPromotion[];
    offer: {
        address: string;
        configuration: {
            show_offer_plans: boolean;
            show_floors_navigator: boolean;
            display_type: OfferDisplayType;
        };
        floors_above_ground_range: IRangeQuery<number> | null;
        geo_point: {
            coordinates: [number, number];
        };
        id: number;
        name: string;
        is_holiday_location: boolean;
        is_condohotel: boolean;
        properties: number;
        slug: string;
        stats: {
            properties_count_for_sale: number;
            properties_with_roi_count: number;
        } | null;
        region: {
            country: Country;
            full_name: string;
            id: number;
        };
        type: OfferType;
        uuid: string;
        vendor: {
            name: string;
            slug: string;
            id: number;
        };
    };
    onButtonClick: (tab: OfferModalActiveTab, source?: GTMEventApplicationSourceSection) => void;
    disableContact?: boolean;
    // offer plans
    isPlanScriptLoading?: boolean;
    onPlansOpenClick?: () => void;
    className?: string;
}

const SCROLL_OFFSET = -(80 + 16); // 80px OfferNavBar height + 16px margin

export const OfferNavBar = React.memo((props: IProps) => {
    const {offer, refElements, promotions, onButtonClick, disableContact} = props;
    const hasStages = useSelector((store: IRPStore) => !!store.offerDetail.offer?.groups?.stages.length);
    const isApartment = offer.is_holiday_location || offer.is_condohotel;
    const isAbroad = offer?.region.country !== Country.POLAND;

    const wrapperRef = useRef<HTMLDivElement>(null);
    const theme = useTheme();
    const {isMobile} = useUserDevice();
    const isSticky = useStickyIntersection(wrapperRef);

    return (
        <div className={props.className} css={navBarWrapperStyle(isSticky)} ref={wrapperRef}>
            <div css={navBarStyle(isSticky)}>
                <OneColumnGridHolder>
                    <OneColumnGridMain css={colStyle}>
                        <nav css={navStyles(isSticky)}>
                            <div css={[opacity0, display("none"), isSticky && !isMobile && showHeader]}>
                                <Text className="bt">{props.offer.name}</Text>
                                <address css={addressHolder}>
                                    <MapMarkerLocationIcon css={mr(2)} fill={theme.colors.gray["700"]} size="2.4" />
                                    <Address variant="body_copy_2" className="bt">
                                        {props.offer.address}
                                    </Address>
                                </address>
                            </div>
                            <div css={brandButtonsWrapperStyle}>
                                <DetailConstructionGalleryCTA
                                    onButtonClick={() =>
                                        onButtonClick(
                                            OfferModalActiveTab.CONSTRUCTION_GALLERY,
                                            isSticky ? GTMEventApplicationSourceSection.BUTTON_STICKY_BAR : GTMEventApplicationSourceSection.BUTTON
                                        )
                                    }
                                />
                                {!isMobile && offer.configuration.show_offer_plans && props.isPlanScriptLoading != null && props.onPlansOpenClick != null && (
                                    <Button
                                        css={OfferDetailBrandButtonStyle}
                                        iconLeft={BrandBuildingCubeIcon}
                                        variant="brand_icon"
                                        size={isMobile ? "medium" : "big"}
                                        onClick={props.onPlansOpenClick}
                                        isLoading={props.isPlanScriptLoading}
                                    >
                                        Plan pięter
                                    </Button>
                                )}
                                <Planner3dModal offer={props.offer} vendorId={props.offer.vendor.id} routedModalId="nav">
                                    <Button
                                        css={OfferDetailBrandButtonStyle}
                                        iconLeft={BrandBuildingCubeIcon}
                                        variant="brand_icon"
                                        size={isMobile ? "medium" : "big"}
                                    >
                                        Plan pięter
                                    </Button>
                                </Planner3dModal>

                                {rpFeatureFlagVendorReportDownload && !isAbroad && <VendorReportButton />}

                                {!hasStages && (
                                    <Button
                                        css={OfferDetailBrandButtonStyle}
                                        iconLeft={BrandMapPinIcon}
                                        variant="brand_icon"
                                        size={isMobile ? "medium" : "big"}
                                        onClick={() => {
                                            onButtonClick(
                                                OfferModalActiveTab.MAP,
                                                isSticky ? GTMEventApplicationSourceSection.BUTTON_STICKY_BAR : GTMEventApplicationSourceSection.BUTTON
                                            );
                                        }}
                                        isLoading={props.isPlanScriptLoading}
                                    >
                                        Zobacz na mapie
                                    </Button>
                                )}
                            </div>
                            {!isMobile && !isSticky && (
                                <Tabs
                                    color="gray"
                                    tabs={[
                                        ...insertIf((offer.stats?.properties_count_for_sale ?? 0) > 0 && refElements.refProperties, {
                                            title: isApartment ? "Apartamenty" : getOfferTypeNamePluralCapital(offer.type),
                                            scrollOffset: SCROLL_OFFSET,
                                            scrollId: "nieruchomosci",
                                            value: "properties",
                                            gtmClick: "Nieruchomości - nawigacja"
                                        }),
                                        ...insertIf(promotions && promotions.length > 0 && refElements.refPromotions, {
                                            title: "Promocje",
                                            scrollOffset: SCROLL_OFFSET,
                                            scrollId: "promocje",
                                            value: "promotions",
                                            gtmClick: "Promocje - nawigacja"
                                        }),
                                        {
                                            title: "Opis",
                                            scrollOffset: SCROLL_OFFSET,
                                            scrollId: "opis",
                                            value: "description",
                                            gtmClick: "Opis - nawigacja"
                                        },
                                        {
                                            title: "Okolica",
                                            scrollOffset: SCROLL_OFFSET,
                                            scrollId: "okolica",
                                            value: "location",
                                            gtmClick: "Okolica - nawigacja"
                                        },
                                        ...insertIf(!disableContact, {
                                            title: "Kontakt",
                                            scrollOffset: SCROLL_OFFSET,
                                            scrollId: "kontakt",
                                            value: "contact",
                                            gtmClick: "Kontakt - nawigacja"
                                        })
                                    ]}
                                    variant="contained"
                                    fullWidth
                                />
                            )}
                        </nav>
                    </OneColumnGridMain>
                </OneColumnGridHolder>
            </div>
        </div>
    );
});

//  Styles

const colStyle = (theme: Theme) => css`
    @media (min-width: ${theme.breakpoints.lg}) {
        max-width: 100%;
        flex: 1 0 0;
        ${ml(0)}
    }
`;
const navBarWrapperStyle = (isSticky: boolean) => (theme: Theme) => css`
    ${isSticky
        ? css`
              z-index: 22; // must be higher than ApplicationSidebar value and checkbox from comparison mode
          `
        : ""}
    padding: 0.8rem 0;

    @media (min-width: ${theme.breakpoints.md}) {
        padding: 1.6rem 0;
        position: sticky;
        top: -1.6rem;
    }
`;
const navBarStyle = (isSticky: boolean) => (theme: Theme) => css`
    padding: 0.8rem 0;

    @media (min-width: ${theme.breakpoints.md}) {
        padding: 0.8rem 0;

        ${isSticky
            ? css`
                  ${elevation(4)}
                  background: #fff;
              `
            : ""}
    }

    @media (min-width: ${theme.breakpoints.md}) and (max-width: 1310px) {
        ${isSticky
            ? css`
                  margin-top: 60px; // other bars height
                  border-top: 1px solid ${getThemeVariable("colors.gray.400")};
              `
            : ""}
    }
`;

const navStyles = (isSticky: boolean) => (theme: Theme) => css`
    width: 100%;
    display: flex;
    ${isSticky ? "flex-direction: row" : "flex-direction: column"};
    justify-content: space-between;
    gap: 2.7rem;

    @media (min-width: ${theme.breakpoints.md}) {
    }
`;

const brandButtonsWrapperStyle = (theme: Theme) => css`
    @media (min-width: ${theme.breakpoints.md}) {
        display: flex;
        justify-content: flex-end;
        ${ml(3)};
        gap: 2.7rem;
    }
`;

export const OfferDetailBrandButtonStyle = css`
    padding: 0;

    &:only-child {
        margin: 0;
    }
`;

const addressHolder = (theme: Theme) => css`
    ${pointer};
    ${flex("center")};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mt()};
    }
`;

const Address = styled(Text)`
    color: ${(props) => props.theme.colors.gray["700"]};
    font-style: normal;
`;

const showHeader = css`
    ${fadeInAnimation("0.2s")};
    display: block;
`;

const opacity0 = css`
    opacity: 0;
`;
import React, {useRef} from "react";
import {useSelector} from "react-redux";
import {css, Theme, useTheme} from "@emotion/react";
import styled from "@emotion/styled";

import {Button} from "@pg-design/button-module";
import {elevation} from "@pg-design/elevation";
import {display, fadeInAnimation, flex, ml, mr, mt, pointer} from "@pg-design/helpers-css";
import {BrandBuildingCubeIcon, BrandMapPinIcon, MapMarkerLocationIcon} from "@pg-design/icons-module";
import {getThemeVariable} from "@pg-design/styles-strings";
import {Tabs} from "@pg-design/tabs";
import {Text} from "@pg-design/text-module";
import {useUserDevice} from "@pg-mono/user-device";
import {Country} from "@pg-rp/common-types";

import {rpFeatureFlagVendorReportDownload} from "../../app/read_rp_environment_variables";
import {IRPStore} from "../../app/rp_reducer";
import {OneColumnGridHolder, OneColumnGridMain} from "../../atoms/OneColumnGrid";
import {useStickyIntersection} from "../../hooks/use_sticky_intersection";
import {GTMEventApplicationSourceSection} from "../../lead/types/GTMEvenApplicationSourceSection";
import {IRangeQuery} from "../../request/IRangeQuery";
import {insertIf} from "../../utils/insert_if";
import {DetailConstructionGalleryCTA} from "../components/DetailConstructionGalleryCTA";
import {VendorReportButton} from "../components/VendorReportButton";
import {IOfferDetailPromotion} from "../detail/actions/fetch_offer_detail_promotions";
import {Planner3dModal} from "../detail/planner3D/components/Planner3D";
import {OfferDisplayType} from "../helpers/OfferDisplayType";
import {getOfferTypeNamePluralCapital} from "../helpers/OfferType";
import {OfferModalActiveTab} from "../types/OfferModalActiveTab";
import {OfferType} from "../types/OfferType";

interface IProps {
    refElements: {
        refGallery: React.RefObject<HTMLTableSectionElement | null>;
        refProperties?: React.RefObject<HTMLTableSectionElement | null>;
        refDescription: React.RefObject<HTMLTableSectionElement | null>;
        refPromotions?: React.RefObject<HTMLTableSectionElement | null>;
        refLocation: React.RefObject<HTMLTableSectionElement | null>;
        refContact?: React.RefObject<HTMLTableSectionElement | null>;
    };
    promotions?: IOfferDetailPromotion[];
    offer: {
        address: string;
        configuration: {
            show_offer_plans: boolean;
            show_floors_navigator: boolean;
            display_type: OfferDisplayType;
        };
        floors_above_ground_range: IRangeQuery<number> | null;
        geo_point: {
            coordinates: [number, number];
        };
        id: number;
        name: string;
        is_holiday_location: boolean;
        is_condohotel: boolean;
        properties: number;
        slug: string;
        stats: {
            properties_count_for_sale: number;
            properties_with_roi_count: number;
        } | null;
        region: {
            country: Country;
            full_name: string;
            id: number;
        };
        type: OfferType;
        uuid: string;
        vendor: {
            name: string;
            slug: string;
            id: number;
        };
    };
    onButtonClick: (tab: OfferModalActiveTab, source?: GTMEventApplicationSourceSection) => void;
    disableContact?: boolean;
    // offer plans
    isPlanScriptLoading?: boolean;
    onPlansOpenClick?: () => void;
    className?: string;
}

const SCROLL_OFFSET = -(80 + 16); // 80px OfferNavBar height + 16px margin

export const OfferNavBar = React.memo((props: IProps) => {
    const {offer, refElements, promotions, onButtonClick, disableContact} = props;
    const hasStages = useSelector((store: IRPStore) => !!store.offerDetail.offer?.groups?.stages.length);
    const isApartment = offer.is_holiday_location || offer.is_condohotel;
    const isAbroad = offer?.region.country !== Country.POLAND;

    const wrapperRef = useRef<HTMLDivElement>(null);
    const theme = useTheme();
    const {isMobile} = useUserDevice();
    const isSticky = useStickyIntersection(wrapperRef);

    return (
        <div className={props.className} css={navBarWrapperStyle(isSticky)} ref={wrapperRef}>
            <div css={navBarStyle(isSticky)}>
                <OneColumnGridHolder>
                    <OneColumnGridMain css={colStyle}>
                        <nav css={navStyles(isSticky)}>
                            <div css={[opacity0, display("none"), isSticky && !isMobile && showHeader]}>
                                <Text className="bt">{props.offer.name}</Text>
                                <address css={addressHolder}>
                                    <MapMarkerLocationIcon css={mr(2)} fill={theme.colors.gray["700"]} size="2.4" />
                                    <Address variant="body_copy_2" className="bt">
                                        {props.offer.address}
                                    </Address>
                                </address>
                            </div>
                            <div css={brandButtonsWrapperStyle}>
                                <DetailConstructionGalleryCTA
                                    onButtonClick={() =>
                                        onButtonClick(
                                            OfferModalActiveTab.CONSTRUCTION_GALLERY,
                                            isSticky ? GTMEventApplicationSourceSection.BUTTON_STICKY_BAR : GTMEventApplicationSourceSection.BUTTON
                                        )
                                    }
                                />
                                {!isMobile && offer.configuration.show_offer_plans && props.isPlanScriptLoading != null && props.onPlansOpenClick != null && (
                                    <Button
                                        css={OfferDetailBrandButtonStyle}
                                        iconLeft={BrandBuildingCubeIcon}
                                        variant="brand_icon"
                                        size={isMobile ? "medium" : "big"}
                                        onClick={props.onPlansOpenClick}
                                        isLoading={props.isPlanScriptLoading}
                                    >
                                        Plan pięter
                                    </Button>
                                )}
                                <Planner3dModal offer={props.offer} vendorId={props.offer.vendor.id} routedModalId="nav">
                                    <Button
                                        css={OfferDetailBrandButtonStyle}
                                        iconLeft={BrandBuildingCubeIcon}
                                        variant="brand_icon"
                                        size={isMobile ? "medium" : "big"}
                                    >
                                        Plan pięter
                                    </Button>
                                </Planner3dModal>

                                {rpFeatureFlagVendorReportDownload && !isAbroad && <VendorReportButton />}

                                {!hasStages && (
                                    <Button
                                        css={OfferDetailBrandButtonStyle}
                                        iconLeft={BrandMapPinIcon}
                                        variant="brand_icon"
                                        size={isMobile ? "medium" : "big"}
                                        onClick={() => {
                                            onButtonClick(
                                                OfferModalActiveTab.MAP,
                                                isSticky ? GTMEventApplicationSourceSection.BUTTON_STICKY_BAR : GTMEventApplicationSourceSection.BUTTON
                                            );
                                        }}
                                        isLoading={props.isPlanScriptLoading}
                                    >
                                        Zobacz na mapie
                                    </Button>
                                )}
                            </div>
                            {!isMobile && !isSticky && (
                                <Tabs
                                    color="gray"
                                    tabs={[
                                        ...insertIf((offer.stats?.properties_count_for_sale ?? 0) > 0 && refElements.refProperties, {
                                            title: isApartment ? "Apartamenty" : getOfferTypeNamePluralCapital(offer.type),
                                            scrollOffset: SCROLL_OFFSET,
                                            scrollId: "nieruchomosci",
                                            value: "properties",
                                            gtmClick: "Nieruchomości - nawigacja"
                                        }),
                                        ...insertIf(promotions && promotions.length > 0 && refElements.refPromotions, {
                                            title: "Promocje",
                                            scrollOffset: SCROLL_OFFSET,
                                            scrollId: "promocje",
                                            value: "promotions",
                                            gtmClick: "Promocje - nawigacja"
                                        }),
                                        {
                                            title: "Opis",
                                            scrollOffset: SCROLL_OFFSET,
                                            scrollId: "opis",
                                            value: "description",
                                            gtmClick: "Opis - nawigacja"
                                        },
                                        {
                                            title: "Okolica",
                                            scrollOffset: SCROLL_OFFSET,
                                            scrollId: "okolica",
                                            value: "location",
                                            gtmClick: "Okolica - nawigacja"
                                        },
                                        ...insertIf(!disableContact, {
                                            title: "Kontakt",
                                            scrollOffset: SCROLL_OFFSET,
                                            scrollId: "kontakt",
                                            value: "contact",
                                            gtmClick: "Kontakt - nawigacja"
                                        })
                                    ]}
                                    variant="contained"
                                    fullWidth
                                />
                            )}
                        </nav>
                    </OneColumnGridMain>
                </OneColumnGridHolder>
            </div>
        </div>
    );
});

//  Styles

const colStyle = (theme: Theme) => css`
    @media (min-width: ${theme.breakpoints.lg}) {
        max-width: 100%;
        flex: 1 0 0;
        ${ml(0)}
    }
`;
const navBarWrapperStyle = (isSticky: boolean) => (theme: Theme) => css`
    ${isSticky
        ? css`
              z-index: 22; // must be higher than ApplicationSidebar value and checkbox from comparison mode
          `
        : ""}
    padding: 0.8rem 0;

    @media (min-width: ${theme.breakpoints.md}) {
        padding: 1.6rem 0;
        position: sticky;
        top: -1.6rem;
    }
`;
const navBarStyle = (isSticky: boolean) => (theme: Theme) => css`
    padding: 0.8rem 0;

    @media (min-width: ${theme.breakpoints.md}) {
        padding: 0.8rem 0;

        ${isSticky
            ? css`
                  ${elevation(4)}
                  background: #fff;
              `
            : ""}
    }

    @media (min-width: ${theme.breakpoints.md}) and (max-width: 1310px) {
        ${isSticky
            ? css`
                  margin-top: 60px; // other bars height
                  border-top: 1px solid ${getThemeVariable("colors.gray.400")};
              `
            : ""}
    }
`;

const navStyles = (isSticky: boolean) => (theme: Theme) => css`
    width: 100%;
    display: flex;
    ${isSticky ? "flex-direction: row" : "flex-direction: column"};
    justify-content: space-between;
    gap: 2.7rem;

    @media (min-width: ${theme.breakpoints.md}) {
    }
`;

const brandButtonsWrapperStyle = (theme: Theme) => css`
    @media (min-width: ${theme.breakpoints.md}) {
        display: flex;
        justify-content: flex-end;
        ${ml(3)};
        gap: 2.7rem;
    }
`;

export const OfferDetailBrandButtonStyle = css`
    padding: 0;

    &:only-child {
        margin: 0;
    }
`;

const addressHolder = (theme: Theme) => css`
    ${pointer};
    ${flex("center")};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mt()};
    }
`;

const Address = styled(Text)`
    color: ${(props) => props.theme.colors.gray["700"]};
    font-style: normal;
`;

const showHeader = css`
    ${fadeInAnimation("0.2s")};
    display: block;
`;

const opacity0 = css`
    opacity: 0;
`;
